import {
  createEntityAdapter,
  EntityAdapter,
  EntityState,
  Update,
} from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { ProjectLocationPlanCameraModel } from '../../models/project-location-plan-camera.model';
import { ProjectLocationPlanCoreActions } from '../project-location-plan-core/project-location-plan-core.actions';
import { ProjectLocationPlanCameraActions } from './project-location-plan-camera.actions';

export interface ProjectLocationPlanCameraState
  extends EntityState<ProjectLocationPlanCameraModel.PlanCameraDocument> {
  isLoading: boolean;
  isSaving: boolean;
  isCopying: boolean;
  isDeleting: boolean;
  hasLoaded: boolean;
}

export const projectLocationPlanCameraAdapter: EntityAdapter<ProjectLocationPlanCameraModel.PlanCameraDocument> =
  createEntityAdapter<ProjectLocationPlanCameraModel.PlanCameraDocument>({
    selectId: (camera) => camera._id,
  });

const initialState: ProjectLocationPlanCameraState =
  projectLocationPlanCameraAdapter.getInitialState({
    isLoading: false,
    hasLoaded: false,
    isSaving: false,
    isCopying: false,
    isDeleting: false,
  });

export const projectLocationPlanCameraStateReducer = createReducer(
  initialState,
  on(ProjectLocationPlanCameraActions.resetToInitialState, () => ({
    ...initialState,
  })),
  on(
    ProjectLocationPlanCameraActions.loadProjectLocationPlanCameras,
    (state) => ({
      ...state,
      isLoading: true,
    }),
  ),
  on(
    ProjectLocationPlanCameraActions.loadProjectLocationPlanCamerasSuccess,
    (state, { cameras }) => {
      return projectLocationPlanCameraAdapter.setAll(cameras, {
        ...state,
        isLoading: false,
        hasLoaded: true,
        activeRequestCoreId: null,
      });
    },
  ),
  //update many
  on(ProjectLocationPlanCameraActions.updateProjectLocationPlanCameras,
    (state) => ({
      ...state,
      isSaving: true,
    }),
  ),
  on(ProjectLocationPlanCameraActions.updateProjectLocationPlanCamerasSuccess,
    (state, { cameraIds, changes }) => {
      const updates: Update<ProjectLocationPlanCameraModel.PlanCameraDocument>[] = cameraIds.map(
        (cameraId) => ({
          id: cameraId,
          changes: changes,
        }),
      );

      return projectLocationPlanCameraAdapter.updateMany(updates, {
        ...state,
        isSaving: false,
      });
    },
  ),
  on(ProjectLocationPlanCameraActions.updateProjectLocationPlanCamerasFail,
    (state) => ({
      ...state,
      isSaving: false,
    }),
  ),
  //update one
  on(ProjectLocationPlanCameraActions.updateProjectLocationPlanCamera,
    (state, { cameraId, changes, optimistic }) => {
      const nextState = {
        ...state,
        isSaving: true,
      };

      if (!optimistic) {
        return nextState;
      }

      return projectLocationPlanCameraAdapter.updateOne(
        {
          id: cameraId,
          changes: changes as Partial<ProjectLocationPlanCameraModel.PlanCameraDocument>,
        },
        nextState,
      );
    },
  ),
  on(ProjectLocationPlanCameraActions.updateProjectLocationPlanCameraSuccess,
    (state, { camera }) => {
      return projectLocationPlanCameraAdapter.setOne(camera, {
        ...state,
        isSaving: false,
      });
    },
  ),
  on(ProjectLocationPlanCameraActions.updateProjectLocationPlanCameraFail,
    (state) => ({
      ...state,
      isSaving: false,
    }),
  ),
  on(ProjectLocationPlanCameraActions.loadProjectLocationPlanCamerasFail,
    (state) => ({
      ...state,
      isLoading: false,
      hasLoaded: true,
    }),
  ),
  on(ProjectLocationPlanCameraActions.loadPlanProjectCamerasSuccess, (state, { cameras }) => {
    return projectLocationPlanCameraAdapter.setAll(cameras, state);
  }),
  on(ProjectLocationPlanCameraActions.savePlanProjectCamera, (state) => ({
    ...state,
    isSaving: true,
  })),
  on(ProjectLocationPlanCameraActions.savePlanProjectCameraSuccess, (state, { camera }) => {
    return projectLocationPlanCameraAdapter.addOne(camera, {
      ...state,
      isSaving: false,
    });
  }),
  on(ProjectLocationPlanCameraActions.savePlanProjectCameraFail, (state) => ({
    ...state,
    isSaving: false,
  })),
  on(ProjectLocationPlanCameraActions.bulkCreateCameras, (state) => ({
    ...state,
    isSaving: true,
  })),
  on(ProjectLocationPlanCameraActions.bulkCreateCamerasSuccess, (state, { cameras }) => {
    return projectLocationPlanCameraAdapter.addMany(cameras, {
      ...state,
      isSaving: false,
    });
  }),
  on(ProjectLocationPlanCameraActions.bulkCreateCamerasFail, (state) => ({
    ...state,
    isSaving: false,
  })),
  on(ProjectLocationPlanCameraActions.copyCamera, (state) => ({
    ...state,
    isCopying: true,
  })),
  on(ProjectLocationPlanCameraActions.copyCameraSuccess, (state, { camera }) => {
    return projectLocationPlanCameraAdapter.setOne(camera, {
      ...state,
      isCopying: false,
    });
  }),
  on(ProjectLocationPlanCameraActions.copyCameraFail, (state) => ({
    ...state,
    isCopying: false,
  })),
  on(ProjectLocationPlanCameraActions.deleteCamera, (state) => ({
    ...state,
    isDeleting: true,
  })),
  on(ProjectLocationPlanCameraActions.deleteCameraSuccess, (state, { camera }) => {
    return projectLocationPlanCameraAdapter.removeOne(camera._id, {
      ...state,
      isDeleting: false,
    });
  }),
  on(ProjectLocationPlanCoreActions.deleteCoreSuccess, (state, { core }) => {
    return projectLocationPlanCameraAdapter.removeMany(
      (camera) => camera.coreId === core._id,
      state,
    );
  }),
  on(ProjectLocationPlanCameraActions.deleteCameraFail, (state) => ({
    ...state,
    isDeleting: false,
  })),
  on(ProjectLocationPlanCameraActions.bulkDeleteProjectLocationPlanCameras, (state) => ({
    ...state,
    isSaving: true,
  })),
  on(ProjectLocationPlanCameraActions.bulkDeleteProjectLocationPlanCamerasSuccess,
    (state, { cameraIds }) => {
      return projectLocationPlanCameraAdapter.removeMany(cameraIds, {
        ...state,
        isSaving: false,
      });
    },),
  on(ProjectLocationPlanCameraActions.bulkDeleteProjectLocationPlanCamerasFail, (state) => ({
    ...state,
    isSaving: false,
  })),
);

export const projectLocationPlanCameraFeature = createFeature({
  name: 'projectLocationPlanCamera',
  reducer: projectLocationPlanCameraStateReducer,
  extraSelectors: ({ selectProjectLocationPlanCameraState }) => {
    const { selectAll, selectEntities } =
      projectLocationPlanCameraAdapter.getSelectors(
        selectProjectLocationPlanCameraState,
      );

    return {
      selectProjectLocationPlanCameraEntities: selectEntities,
      selectProjectLocationPlanCameras: selectAll,
      selectProjectLocationPlanCameraById: (cameraId: string) =>
        createSelector(selectEntities, (entities) => {
          return entities[cameraId] ?? null;
        }),
      selectProjectLocationPlanCamerasByCoreId: (coreId: string) =>
        createSelector(selectAll, (cameras) => {
          return cameras.filter((camera) => camera.coreId === coreId);
        }),
      selectProjectLocationPlanCamerasByPlanId: (planId: string) =>
        createSelector(selectAll, (cameras) => {
          return cameras.filter((camera) => camera.planId === planId);
        }),
    };
  },
});
